import { getInitialSelectedVariant } from '@src/components/product-card/product-card.helpers'
import { IProductCard, IWebsiteProduct } from '@src/components/product-card/product-card.types'
import {
  Filter,
  FilterOptions,
  SelectedFilter,
  UpdateQueryParams,
} from '@src/pages/[...slug]/components/collection/domains/CollectionFilter'
import {
  getIndividualTagStatus,
  ProductTagKey,
} from '@src/quince-components/src/molecules/productCard/productCard.helpers'
import { createTargetURL } from '@src/quince-site-common/src/hooks/useQueryState'
import searchCacheStateManager from '@src/quince-site-common/src/redux/globalStateManager/SearchCacheStateManager'
import {
  Filter as SearchFilter,
  Facets,
  FilterAttribute,
  FilterOperation,
} from '@src/quince-site-common/src/services/sandSearchService/searchService.types'
import { isBrowser } from '@src/quince-site-common/src/utils/windowUtil'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import { transformTagsIntoAnalyticsTags } from '@src/utils/general.utils'

import { PRODUCTS_LIMIT_FOR_EVENT, SEARCH_WATCHED_QUERY_KEYS } from './searchResults/searchResults.constants'
import { isItemInWishList } from './searchResults/searchResults.helpers'

const DEFAULT_SELECTION_TYPE = 'Checkbox'

// This map is maintained to support backward compatibility for URL.
export const FILTER_TO_FACET_KEYS = {
  [FilterAttribute.COLOR]: 'colors',
  [FilterAttribute.PRICE]: 'prices',
  [FilterAttribute.DEPARTMENT]: 'departments',
  [FilterAttribute.CATEGORY]: 'categories',
  [FilterAttribute.MATERIAL]: 'materials',
  [FilterAttribute.SIZE]: 'sizes',
} as const

const FACET_TO_FILTER_KEYS = Object.fromEntries(
  Object.entries(FILTER_TO_FACET_KEYS).map(([key, value]) => [value, key]),
) as Record<string, FilterAttribute>

export const getFilterAttributeFromAPIKey = (filterAttribute: FilterAttribute): string => {
  return FILTER_TO_FACET_KEYS[filterAttribute]
}

export const getAPIFilterKey = (stringKey: string): FilterAttribute => {
  return FACET_TO_FILTER_KEYS[stringKey] ?? (stringKey as FilterAttribute)
}

export function getFilterOperation(key: FilterAttribute) {
  if (key === FilterAttribute.PRICE) {
    return FilterOperation.RANGE
  }

  return FilterOperation.EQ
}

export function convertFacetsToInitialFilters(facetsList?: Facets[]): Filter[] {
  if (!facetsList) return []

  return facetsList
    .map(facet => {
      const values = facet.valueMetadata.map(metadata => {
        const filterValue = new FilterOptions({
          name: metadata.value,
          displayName: metadata.displayName,
          value: metadata.value,
          hex: metadata.hex,
          imgURL: metadata.imgURL,
        })
        return filterValue
      })

      return new Filter({
        name: getFilterAttributeFromAPIKey(facet.key),
        config: {
          columnsToShow: facet.config.columnsToShow ?? 1,
          label: facet.config.label,
          type: facet.config.selectionType ?? DEFAULT_SELECTION_TYPE,
        },
        values,
      })
    })
    .filter(filter => filter.values.length > 0)
}

// Convert selected search filters to API filter format
export const getSearchFilters = (selectedFilters: SelectedFilter): SearchFilter[] => {
  const filters: SearchFilter[] = []

  Object.keys(selectedFilters).forEach(key => {
    const filterKey = getAPIFilterKey(key)
    const values = SelectedFilter.getSelectedFilterValuesByFilterName(selectedFilters, key)
    if (values.length > 0) {
      filters.push({
        key: filterKey,
        value: values,
        operation: getFilterOperation(filterKey),
      })
    }
  })

  return filters
}

// Handle filter Updates
export const getUpdatedSelectedFilters = ({
  filter,
  selectedFilters,
}: {
  filter: UpdateQueryParams['filter']
  selectedFilters: SelectedFilter
}) => {
  let updatedSelectedFilters = selectedFilters
  if (filter) {
    switch (filter.type) {
      case 'remove': {
        updatedSelectedFilters = SelectedFilter.removeValueFromSelectedFilter(
          selectedFilters,
          filter.value,
          filter.name,
        )
        break
      }
      case 'toggle': {
        updatedSelectedFilters = SelectedFilter.toggleValueFromSelectedFilter(
          selectedFilters,
          filter.name,
          filter.value,
        )
        trackEvent(TELEMETRY_EVENT_NAMES.FILTER_CLICK, {
          label: filter.name,
          value: filter.value,
          context: {
            isSelected: SelectedFilter.isFilterValueSelected(updatedSelectedFilters, filter.name, filter.value),
            selectedFilters: updatedSelectedFilters,
          },
        })
        break
      }
      case 'reset': {
        updatedSelectedFilters = new SelectedFilter({})
        break
      }
      default:
        break
    }
  }

  return updatedSelectedFilters
}

export const getSearchCompletedEventData = ({
  data,
  searchQuery,
  numberOfSearchResults,
}: {
  data: IWebsiteProduct[]
  searchQuery: string
  numberOfSearchResults: number
}) => {
  if (data.length) {
    let oos_product_count = 0
    const products = data.slice(0, PRODUCTS_LIMIT_FOR_EVENT).map((product, index) => {
      const { classification, relatedIds, title, colorVariants, reviews } = product
      const initialSelectedVariant = getInitialSelectedVariant({ cardVariants: product.cardVariants })
      let productData = {
        product_id: relatedIds.spid,
        name: title,
        position: index + 1,
        color: initialSelectedVariant?.displayConfig.value,
        count_color_variant: colorVariants?.length,
        rating: reviews.averageRating,
        tags: initialSelectedVariant?.tags?.map(item => item.value) ?? [],
        tag_ids: transformTagsIntoAnalyticsTags(initialSelectedVariant?.tags?.map(item => item.value) ?? []),
        department: classification?.department,
        division: classification?.division,
        subdepartment: classification?.subdepartment,
        wishListed: isItemInWishList(product),
      }
      // eslint-disable-next-line i18next/no-literal-string
      if (productData.tags.includes('Sold Out')) {
        oos_product_count++
      }
      productData = { ...productData, ...getIndividualTagStatus(productData.tags as ProductTagKey[]) }

      return productData
    })

    return {
      products,
      result_count: products.length,
      null_search: false,
      oos_product_count,
      searchQuery,
      queryLength: searchQuery.length,
      numberOfSearchResults,
    }
  } else {
    return {
      null_search: true,
      result_count: 0,
      products: [],
      searchQuery,
      queryLength: searchQuery.length,
      numberOfSearchResults,
    }
  }
}

export const transformProductsToProductCards = (products: IWebsiteProduct[]): IProductCard[] => {
  return products.map(product => ({ product }))
}

export const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

export const getSearchCacheKey = (searchParams: URLSearchParams) => {
  let cacheKey = ''
  SEARCH_WATCHED_QUERY_KEYS.forEach(key => {
    if (searchParams.get(key)) {
      cacheKey += `${key}=${searchParams.get(key)}&`
    }
  })

  return cacheKey
}

export const getDestinationSearchCacheKey = ({
  searchParams,
  updatedWatchedQueryState,
}: {
  searchParams: URLSearchParams
  updatedWatchedQueryState: { q: string; filter: string }
}) => {
  if (!isBrowser()) return

  const targetURLSearchParams = createTargetURL({
    updatedWatchedQueryState,
    searchParams: searchParams,
    queryKeys: SEARCH_WATCHED_QUERY_KEYS,
  })

  const destinationSearchParams = new URLSearchParams(targetURLSearchParams)
  return getSearchCacheKey(destinationSearchParams)
}

export const resetSearchCacheOnFilterChange = ({
  searchParams,
  updatedWatchedQueryState,
}: {
  searchParams: URLSearchParams
  updatedWatchedQueryState: { q: string; filter: string }
}) => {
  if (!isBrowser()) return

  const targetURLSearchParams = createTargetURL({
    updatedWatchedQueryState,
    searchParams: searchParams,
    queryKeys: SEARCH_WATCHED_QUERY_KEYS,
  })

  const destinationSearchParams = new URLSearchParams(targetURLSearchParams)
  const cacheKey = getSearchCacheKey(destinationSearchParams)
  const searchCache = searchCacheStateManager.getSearchCache(cacheKey)
  if (searchCache) {
    searchCacheStateManager.publishSearchCache(cacheKey, 0)
  }
}
